/**
 * Email Quote Selection Dialog
 * Allows users to select and preview emails for automatic quote parsing
 * Integrates with QuoteEmailFetcher and SupplierQuoteEmailParser
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  Paper
} from '@mui/material';
import {
  Email as EmailIcon,
  Visibility as PreviewIcon,
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon
} from '@mui/icons-material';

import { QuoteEmailFetcher, type QuoteEmailCandidate } from '../../../services/quoteEmailFetcher';
import { SupplierQuoteEmailParser, type ParsedSupplierQuote } from '../../../services/supplierQuoteEmailParser';
import { AutomaticWorkflowService } from '../../../services/automaticWorkflowService';

interface EmailQuoteSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onEmailSelected: (parsedData: ParsedSupplierQuote, rawEmail: QuoteEmailCandidate) => void;
  caseId: string;
  currentWorkflowState?: string;
  title?: string;
}

interface EmailPreview {
  email: QuoteEmailCandidate;
  parsedData?: ParsedSupplierQuote;
  parsing?: boolean;
}

const EmailQuoteSelectionDialog: React.FC<EmailQuoteSelectionDialogProps> = ({
  open,
  onClose,
  onEmailSelected,
  caseId,
  currentWorkflowState,
  title = 'E-Mail für Angebots-Parsing auswählen'
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailCandidates, setEmailCandidates] = useState<QuoteEmailCandidate[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<QuoteEmailCandidate | null>(null);
  const [previewData, setPreviewData] = useState<EmailPreview | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  // Fetch email candidates when dialog opens
  useEffect(() => {
    if (open && caseId) {
      fetchEmailCandidates();
    }
  }, [open, caseId]);

  const fetchEmailCandidates = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log(`🔍 Fetching quote email candidates for case ${caseId}...`);
      const candidates = await QuoteEmailFetcher.fetchQuoteEmailCandidates(caseId);
      setEmailCandidates(candidates);
      
      if (candidates.length === 0) {
        setError('Keine E-Mails mit Angebots-Inhalt gefunden. Stellen Sie sicher, dass Angebots-E-Mails von Lieferanten eingegangen sind.');
      } else {
        console.log(`✅ Found ${candidates.length} email candidates for quote parsing`);
      }
    } catch (err) {
      console.error('❌ Error fetching email candidates:', err);
      setError(err instanceof Error ? err.message : 'Fehler beim Laden der E-Mails');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSelect = (email: QuoteEmailCandidate) => {
    setSelectedEmail(email);
    setShowPreview(false);
    setPreviewData(null);
  };

  const handlePreviewEmail = async (email: QuoteEmailCandidate) => {
    setPreviewData({
      email,
      parsing: true
    });
    setShowPreview(true);

    try {
      console.log(`📊 Parsing email from ${email.from_address} for preview...`);
      const parsedData = SupplierQuoteEmailParser.parseSupplierQuote({
        from_address: email.from_address,
        subject: email.subject,
        body_text: email.body_text,
        body_html: email.body_html
      });

      setPreviewData({
        email,
        parsedData,
        parsing: false
      });
      
      console.log(`✅ Email parsing complete. Confidence: ${parsedData.extractionMetadata.confidence}, Positions: ${parsedData.positions.length}`);
    } catch (err) {
      console.error('❌ Error parsing email:', err);
      setPreviewData({
        email,
        parsing: false
      });
    }
  };

  const handleConfirmSelection = async () => {
    if (selectedEmail && previewData?.parsedData) {
      console.log(`✅ User confirmed email selection from ${selectedEmail.from_address}`);
      
      // Call the original callback
      onEmailSelected(previewData.parsedData, selectedEmail);
      
      // Trigger automatic workflow transition if in supplier_response_received state
      if (currentWorkflowState === 'supplier_response_received') {
        console.log(`🚀 Attempting auto-transition: supplier_response_received → quote_selected`);
        try {
          const success = await AutomaticWorkflowService.triggerSupplierEvent(
            caseId,
            currentWorkflowState,
            'QUOTE_PARSED_AND_SELECTED',
            {
              selectedEmail: {
                from: selectedEmail.from_address,
                subject: selectedEmail.subject,
                date: selectedEmail.received_at
              },
              parsedQuoteData: {
                positions: previewData.parsedData.positions.length,
                totalValue: previewData.parsedData.quoteInfo.total_quote_value,
                supplier: previewData.parsedData.supplierInfo.supplier_name,
                confidence: previewData.parsedData.extractionMetadata.confidence
              }
            }
          );
          
          if (success) {
            console.log(`✅ Auto-transition completed: supplier_response_received → quote_selected`);
          } else {
            console.log(`⚠️ Auto-transition failed, manual transition may be required`);
          }
        } catch (error) {
          console.error('Error during auto-transition:', error);
        }
      }
      
      onClose();
    }
  };

  const getConfidenceIcon = (likelihood: string) => {
    switch (likelihood) {
      case 'high': return <CheckIcon sx={{ color: 'success.main', fontSize: 16 }} />;
      case 'medium': return <WarningIcon sx={{ color: 'warning.main', fontSize: 16 }} />;
      case 'low': return <ErrorIcon sx={{ color: 'error.main', fontSize: 16 }} />;
      default: return null;
    }
  };

  const getConfidenceColor = (likelihood: string) => {
    switch (likelihood) {
      case 'high': return 'success';
      case 'medium': return 'warning';
      case 'low': return 'error';
      default: return 'default';
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px', maxHeight: '90vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EmailIcon color="primary" />
          <Typography variant="h6">{title}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>E-Mails werden analysiert...</Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && (
          <Box sx={{ display: 'flex', gap: 2, height: '500px' }}>
            {/* Email List */}
            <Box sx={{ flex: 1, minWidth: '400px' }}>
              <Typography variant="subtitle1" gutterBottom>
                E-Mail-Kandidaten ({emailCandidates.length})
              </Typography>
              
              <List sx={{ maxHeight: '460px', overflow: 'auto' }}>
                {emailCandidates.map((email) => {
                  const preview = QuoteEmailFetcher.formatEmailPreview(email);
                  const isSelected = selectedEmail?.id === email.id;
                  
                  return (
                    <ListItem
                      key={email.id}
                      sx={{
                        border: 1,
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        borderRadius: 1,
                        mb: 1,
                        bgcolor: isSelected ? 'primary.50' : 'background.paper',
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: isSelected ? 'primary.100' : 'grey.50'
                        }
                      }}
                      onClick={() => handleEmailSelect(email)}
                    >
                      <ListItemIcon>
                        {getConfidenceIcon(email.quoteLikelihood)}
                      </ListItemIcon>
                      
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="subtitle2" noWrap sx={{ flex: 1 }}>
                              {preview.title}
                            </Typography>
                            <Chip 
                              label={preview.confidence}
                              size="small"
                              color={getConfidenceColor(email.quoteLikelihood) as any}
                              variant="outlined"
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              {preview.subtitle}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ 
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              fontSize: '0.75rem',
                              lineHeight: 1.2
                            }}>
                              {preview.preview}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                              {preview.metadata.map((meta: string, idx: number) => (
                                <Chip key={idx} label={meta} size="small" variant="outlined" />
                              ))}
                            </Box>
                          </Box>
                        }
                      />
                      
                      <Tooltip title="Vorschau & Parsing">
                        <IconButton 
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePreviewEmail(email);
                          }}
                        >
                          <PreviewIcon />
                        </IconButton>
                      </Tooltip>
                    </ListItem>
                  );
                })}
              </List>
            </Box>

            {/* Preview Panel */}
            {showPreview && previewData && (
              <>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ flex: 1, minWidth: '400px' }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Parsing-Vorschau
                  </Typography>
                  
                  <Paper sx={{ p: 2, height: '460px', overflow: 'auto' }}>
                    {previewData.parsing ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <CircularProgress size={24} />
                        <Typography sx={{ ml: 2 }}>E-Mail wird geparst...</Typography>
                      </Box>
                    ) : previewData.parsedData ? (
                      <Box>
                        {/* Confidence & Summary */}
                        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Parsing-Ergebnis
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <Chip 
                              label={`Vertrauen: ${previewData.parsedData.extractionMetadata.confidence}`}
                              color={getConfidenceColor(previewData.parsedData.extractionMetadata.confidence) as any}
                              size="small"
                            />
                            <Chip 
                              label={`${previewData.parsedData.positions.length} Positionen`}
                              size="small"
                              variant="outlined"
                            />
                            <Chip 
                              label={previewData.parsedData.extractionMetadata.detectedLanguage.toUpperCase()}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                        </Box>

                        {/* Supplier Information */}
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>Lieferant</Typography>
                          <Typography variant="body2">
                            <strong>Name:</strong> {previewData.parsedData.supplierInfo.supplier_name || 'Nicht erkannt'}
                          </Typography>
                          <Typography variant="body2">
                            <strong>E-Mail:</strong> {previewData.parsedData.supplierInfo.supplier_email}
                          </Typography>
                          {previewData.parsedData.supplierInfo.company && (
                            <Typography variant="body2">
                              <strong>Firma:</strong> {previewData.parsedData.supplierInfo.company}
                            </Typography>
                          )}
                        </Box>

                        {/* Quote Information */}
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>Angebots-Info</Typography>
                          {previewData.parsedData.quoteInfo.total_quote_value && (
                            <Typography variant="body2">
                              <strong>Gesamtwert:</strong> {previewData.parsedData.quoteInfo.total_quote_value} {previewData.parsedData.quoteInfo.currency}
                            </Typography>
                          )}
                          {previewData.parsedData.quoteInfo.delivery_time_days && (
                            <Typography variant="body2">
                              <strong>Lieferzeit:</strong> {previewData.parsedData.quoteInfo.delivery_time_days} Tage
                            </Typography>
                          )}
                          {previewData.parsedData.quoteInfo.payment_terms && (
                            <Typography variant="body2">
                              <strong>Zahlungsbedingungen:</strong> {previewData.parsedData.quoteInfo.payment_terms}
                            </Typography>
                          )}
                        </Box>

                        {/* Positions */}
                        {previewData.parsedData.positions.length > 0 && (
                          <Box>
                            <Typography variant="subtitle2" gutterBottom>
                              Positionen ({previewData.parsedData.positions.length})
                            </Typography>
                            <List dense>
                              {previewData.parsedData.positions.slice(0, 5).map((position: any, index: number) => (
                                <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                                  <ListItemText
                                    primary={`${position.position_number}. ${position.part_name || 'Unbenannt'}`}
                                    secondary={
                                      <Box sx={{ display: 'flex', gap: 2 }}>
                                        {position.unit_price && (
                                          <span>€{position.unit_price.toFixed(2)}</span>
                                        )}
                                        {position.delivery_days && (
                                          <span>{position.delivery_days} Tage</span>
                                        )}
                                      </Box>
                                    }
                                  />
                                </ListItem>
                              ))}
                              {previewData.parsedData.positions.length > 5 && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                  ... und {previewData.parsedData.positions.length - 5} weitere Positionen
                                </Typography>
                              )}
                            </List>
                          </Box>
                        )}

                        {/* Extracted Fields Summary */}
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Erkannte Felder: {previewData.parsedData.extractionMetadata.extractedFields.join(', ')}
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Alert severity="error">
                        Fehler beim Parsen der E-Mail. Versuchen Sie eine andere E-Mail.
                      </Alert>
                    )}
                  </Paper>
                </Box>
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>
          Abbrechen
        </Button>
        <Button 
          variant="contained"
          onClick={handleConfirmSelection}
          disabled={!selectedEmail || !previewData?.parsedData || previewData.parsing}
          startIcon={<CheckIcon />}
        >
          E-Mail verwenden
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailQuoteSelectionDialog;
